import { useMutation, useQuery } from "@tanstack/vue-query";
import type { MeResponse } from "~/types/user";

export const useAuthParticipent = defineStore("authParticipent", () => {
  const { $axios, $config, $reportError } = useNuxtApp();
  const toast = useMessage();

  const route = useRoute();
  const pathSplit = route.fullPath.split("/");
  const model_type = ref(pathSplit[3]);
  const subdomain = window.location.hostname.split(".").slice(0, -2).join(".");

  const account = ref<any>(null);
  const user = ref<any>(null);
  const me_auth = ref<any>(null);
  const kbaDataSent = ref<Record<string, any>>({});
  const shouldShowKbaErrorModal = ref<boolean>(false);
  const shouldLockButtonForever = computed<boolean>(() => {
    return user.value?.kba_last_error?.completelyLockButton ?? false;
  });


  const { data: accountData, refetch: refetchAccount } = useQuery({
    queryKey: ["account", subdomain],
    queryFn: ({ queryKey }) => {
      return $axios.get(`/v1/accounts/${queryKey[1]}`).then((res) => res.data);
    },
    enabled: computed(() => Boolean(subdomain && route.meta.middleware === "participent")),
  });

  watch(
    () => accountData.value,
    (value) => {
      account.value = value;
    },
  );

  const {
    mutateAsync: refetch,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: async () =>
      await $axios
        .get(
          `/v1/sessions/${route.params.id}/participants/${route.params.user_id}`,
        )
        .then((res) => res.data),
    onSuccess: (data) => {
      user.value = data;
    },
    onError: (error) => {
      user.value = null;
      console.log(error);
    },
  });


  const {
    mutateAsync: meRefetch,
    isSuccess: meIsSuccess,
    isPending: meIsPending,
  } = useMutation({
    mutationFn: async () =>
      await $axios.get<MeResponse>("/user").then((res) => res.data),
    onSuccess: (data) => {
      try {
        if (!data.user || !data.type) {
          throw new Error('The user information was not loaded correctly.');
        }
        // Assign it if it has a value.
        me_auth.value = data.user ?? null;
      } catch (error) {
        $reportError(error);
        toast.message({
          title: "Error",
          message: "An unexpected error occurred trying to load the user information.",
        });
        setTimeout(() => {
          navigateTo(`${$config.public.primaryURL}/login`, {
            external: true,
          });
        }, 2000);
      }
    },
    onError: (error) => {
      me_auth.value = null;
      console.log(error);
    },
  });

  const { mutateAsync: logout, isSuccess: isLoggedOut } = useMutation({
    mutationFn: async (data) => {
      await $axios.post("/logout");
    },
    onSuccess: () => {
      user.value = null;
      navigateTo(
        `/sessions/${route.params.id}/${model_type.value}/${route.params.user_id}`,
        {external: true}
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutateAsync: logoutNoRoute, isSuccess: isLoggedOutRoute } =
    useMutation({
      mutationFn: async (data) => {
        await $axios.post("/logout");
      },
      onSuccess: () => {
        user.value = null;
      },
      onError: (error) => {
        console.log(error);
      },
    });

  return {
    logoutNoRoute,
    refetchAccount,
    model_type,
    user,
    isPending,
    refetch,
    isLoggedOut,
    isSuccess,
    logout,
    subdomain,
    account,
    me_auth,
    meRefetch,
    meIsSuccess,
    meIsPending,
    shouldShowKbaErrorModal,
    shouldLockButtonForever,
    kbaDataSent,
  };
});
